import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { saveAs } from 'Core/utils';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { PartService } from '../../../service/api/part-service';
const { PARTS_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'parts';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  product: null,
  printersdata: [],
  uploadDrawingVisible: false,
  filters: {
    status: {
      value: 'In Part',
      label: 'In Part'
    },
    searchText: ''
  },
  filterVisible: false,
  tileView: true,
  selectedPart: null,
  editModalVisible: false,

};

export const fetchParts = createAsyncThunk(
  'parts/fetchParts',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await PartService.getAllPartsList(filters);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Parts');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Parts');
    }
  },
);

export const getPartDetailsById = createAsyncThunk(
  'parts/getPartDetailsById',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await PartService.getPartDetailsById(filters);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Part detail');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Part detail');
    }
  },
);

/** Same API as getPartDetailsById but does not open the add/edit modal (used before Part print). */
export const getPartDetailsByIdForPrint = createAsyncThunk(
  'parts/getPartDetailsByIdForPrint',
  async (partId, { rejectWithValue }) => {
    try {
      const response = await PartService.getPartDetailsById(partId);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Part detail');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Part detail');
    }
  },
);

export const addPart = createAsyncThunk(
  'parts/addPart',
  async (partPayload, { rejectWithValue }) => {
    try {
      const response = await PartService.AddPart(partPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add Parts');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add Parts');
    }
  },
);

export const editPart = createAsyncThunk(
  'parts/editPart',
  async (partPayload, { rejectWithValue }) => {
    try {
      const response = await PartService.AddPart(partPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update part');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update part');
    }
  },
);

export const downloadParts = createAsyncThunk(
  'parts/downloadParts',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await PartService.downloadPart(filters);
      const blob = response;
      saveAs(blob, `Parts.xlsx`);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to download Parts');
    }
  },
);

export const deletePart = createAsyncThunk(
  'parts/deletePart',
  async (partId, { rejectWithValue }) => {
    try {
      const response = await PartService.deletePart(partId);
      if (response.status === 200) {
        return partId;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete part');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete part');
    }
  },
);

export const fetchPartsWithFilters = () => (dispatch, getState) => {
  const filters = getState()[STORE_KEY].filters;
  dispatch(
    fetchParts({
      searchText: filters.searchText,
      status: filters.status.value
    }),
  );
};

export const downloadPartsWithFilters = () => (dispatch, getState) => {
  const filters = getState()[STORE_KEY].filters;
  dispatch(
    downloadParts({
      searchText: filters.searchText,
      customer: filters?.customer?.value,
      status: filters?.status?.value,
      partDate: filters?.shipDate,
    }),
  );
};


export const { reducer, actions } = createSlice({
  name: 'parts',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setProduct: (state, action) => {
      state.product = action.payload;
    },
    resetFilters: (state) => {
      state.filters = INITIAL_STATE.filters;
    },
    setUploadDrawingVisible: (state, action) => {
      console.log('uploadDrawingVisible', action.payload)
      state.uploadDrawingVisible = action.payload;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setCustomer: (state, action) => {
      state.filters.customer = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setStartDate: (state, action) => {
      state.filters.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.filters.endDate = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload; // Add reducer to update tileView
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchParts.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchParts.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getPartDetailsById.fulfilled, (state, action) => {
        state.product = action.payload;
      })
      .addCase(addPart.fulfilled, (state, action) => {
        const newPart = {
          ...action.payload,
          isNewlyCreated: true,
        };
        state.data = [newPart, ...state.data];
      })
      .addCase(addPart.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editPart.fulfilled, (state, action) => {
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editPart.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deletePart.fulfilled, (state, action) => {
        const newData = state.data.filter(s => s.id !== action.payload);
        state.data = newData;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addPart.rejected, editPart.rejected
  ),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addPart.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(PARTS_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Part saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(getPartDetailsById.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(PARTS_ADD_EDIT, action.payload));
  },
});

redux.listener.startListening({
  matcher: isAnyOf(getPartDetailsById.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(showModal(PARTS_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Part detail loading failed',
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editPart.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(PARTS_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Part updated successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(deletePart.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Part deleted successfully',
        type: 'success',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);
